import React from "react";

const GraphDisplay = ({ graphData, theme }) => {
  if (!graphData) {
    return (
      <div className="h-96 flex items-center justify-center border-2 border-dashed border-muted-foreground/30 rounded-lg">
        <p className="text-muted-foreground">係数を入力してグラフを表示</p>
      </div>
    );
  }

  // SVGの座標変換関数
  const mapToSVG = (x, y, width = 400, height = 400, xRange = 20, yRange = 20) => {
    const svgX = ((x + xRange/2) / xRange) * width;
    const svgY = height - ((y + yRange/2) / yRange) * height;
    return { x: svgX, y: svgY };
  };

  // 二次曲線のパスを生成
  const generateCurvePath = () => {
    const { A, B, C, D, E, F } = graphData.coefficients;
    const width = 400;
    const height = 400;
    
    let pathElements = [];
    
    // 円や楕円の場合のパラメトリック描画
    if (Math.abs(B) < 1e-10) {
      // 回転していない場合
      if (Math.abs(A - C) < 1e-10 && A > 0) {
        // 円の場合
        const h = -D / (2 * A);
        const k = -E / (2 * C);
        const r2 = h * h + k * k - F / A;
        
        if (r2 > 0) {
          const r = Math.sqrt(r2);
          const center = mapToSVG(h, k, width, height);
          const radius = (r / 10) * (width / 2);
          
          return `M ${center.x + radius} ${center.y} A ${radius} ${radius} 0 1 1 ${center.x - radius} ${center.y} A ${radius} ${radius} 0 1 1 ${center.x + radius} ${center.y}`;
        }
      } else if (A > 0 && C > 0) {
        // 楕円の場合
        const h = -D / (2 * A);
        const k = -E / (2 * C);
        const constant = A * h * h + C * k * k + F;
        
        if (constant < 0) {
          const a2 = -constant / A;
          const b2 = -constant / C;
          
          if (a2 > 0 && b2 > 0) {
            const a = Math.sqrt(a2);
            const b = Math.sqrt(b2);
            const center = mapToSVG(h, k, width, height);
            const rx = (a / 10) * (width / 2);
            const ry = (b / 10) * (height / 2);
            
            return `M ${center.x + rx} ${center.y} A ${rx} ${ry} 0 1 1 ${center.x - rx} ${center.y} A ${rx} ${ry} 0 1 1 ${center.x + rx} ${center.y}`;
          }
        }
      }
    }
    
    // 一般的な場合：点を計算してパスを生成
    const points = [];
    const resolution = 400;
    
    for (let i = 0; i < resolution; i++) {
      const x = -10 + (20 * i) / (resolution - 1);
      
      if (Math.abs(C) > 1e-10) {
        const a = C;
        const b = B * x + E;
        const c = A * x * x + D * x + F;
        const discriminant = b * b - 4 * a * c;
        
        if (discriminant >= 0) {
          const sqrt_disc = Math.sqrt(discriminant);
          const y1 = (-b + sqrt_disc) / (2 * a);
          const y2 = (-b - sqrt_disc) / (2 * a);
          
          if (Math.abs(y1) <= 10) {
            points.push({ x, y: y1 });
          }
          if (Math.abs(y2) <= 10 && Math.abs(y1 - y2) > 1e-6) {
            points.push({ x, y: y2 });
          }
        }
      }
    }
    
    // 放物線の場合は別処理
    if (Math.abs(C) < 1e-10 && Math.abs(A) > 1e-10) {
      points.length = 0; // リセット
      for (let i = 0; i < resolution; i++) {
        const y = -10 + (20 * i) / (resolution - 1);
        const a = A;
        const b = B * y + D;
        const c = C * y * y + E * y + F;
        const discriminant = b * b - 4 * a * c;
        
        if (discriminant >= 0) {
          const sqrt_disc = Math.sqrt(discriminant);
          const x1 = (-b + sqrt_disc) / (2 * a);
          const x2 = (-b - sqrt_disc) / (2 * a);
          
          if (Math.abs(x1) <= 10) {
            points.push({ x: x1, y });
          }
          if (Math.abs(x2) <= 10 && Math.abs(x1 - x2) > 1e-6) {
            points.push({ x: x2, y });
          }
        }
      }
    }
    
    if (points.length === 0) return "";
    
    // 点をソートして滑らかな曲線を作成
    points.sort((a, b) => {
      if (Math.abs(a.x - b.x) < 1e-6) {
        return a.y - b.y;
      }
      return a.x - b.x;
    });
    
    let path = "";
    let currentPath = [];
    
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      const svgPoint = mapToSVG(point.x, point.y, width, height);
      
      if (currentPath.length === 0) {
        currentPath.push(`M ${svgPoint.x} ${svgPoint.y}`);
      } else {
        const lastPoint = points[i - 1];
        if (Math.abs(point.x - lastPoint.x) < 0.1 && Math.abs(point.y - lastPoint.y) < 2) {
          currentPath.push(`L ${svgPoint.x} ${svgPoint.y}`);
        } else {
          path += currentPath.join(" ") + " ";
          currentPath = [`M ${svgPoint.x} ${svgPoint.y}`];
        }
      }
    }
    
    if (currentPath.length > 0) {
      path += currentPath.join(" ");
    }
    
    return path;
  };

  const svgPath = generateCurvePath();
  const gridColor = theme === "dark" ? "#374151" : "#e5e7eb";
  const axisColor = theme === "dark" ? "#6b7280" : "#9ca3af";
  const textColor = theme === "dark" ? "#ffffff" : "#000000";

  return (
    <div className="h-96 w-full">
      <svg 
        width="100%" 
        height="100%" 
        viewBox="0 0 400 400" 
        className="border rounded-lg"
        style={{ backgroundColor: theme === "dark" ? "#1f2937" : "#ffffff" }}
      >
        {/* グリッド */}
        <defs>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke={gridColor} strokeWidth="1"/>
          </pattern>
        </defs>
        <rect width="400" height="400" fill="url(#grid)" />
        
        {/* 軸 */}
        <line x1="0" y1="200" x2="400" y2="200" stroke={axisColor} strokeWidth="2" />
        <line x1="200" y1="0" x2="200" y2="400" stroke={axisColor} strokeWidth="2" />
        
        {/* 軸ラベル */}
        <text x="390" y="195" fill={textColor} fontSize="12">x</text>
        <text x="205" y="15" fill={textColor} fontSize="12">y</text>
        
        {/* 目盛り */}
        {[-8, -6, -4, -2, 2, 4, 6, 8].map(val => (
          <g key={`x-${val}`}>
            <line 
              x1={200 + (val * 20)} 
              y1="195" 
              x2={200 + (val * 20)} 
              y2="205" 
              stroke={axisColor} 
              strokeWidth="1" 
            />
            <text 
              x={200 + (val * 20)} 
              y="220" 
              fill={textColor} 
              fontSize="10" 
              textAnchor="middle"
            >
              {val}
            </text>
          </g>
        ))}
        
        {[-8, -6, -4, -2, 2, 4, 6, 8].map(val => (
          <g key={`y-${val}`}>
            <line 
              x1="195" 
              y1={200 - (val * 20)} 
              x2="205" 
              y2={200 - (val * 20)} 
              stroke={axisColor} 
              strokeWidth="1" 
            />
            <text 
              x="185" 
              y={200 - (val * 20) + 3} 
              fill={textColor} 
              fontSize="10" 
              textAnchor="middle"
            >
              {val}
            </text>
          </g>
        ))}
        
        {/* 二次曲線 */}
        {svgPath && (
          <path 
            d={svgPath} 
            fill="none" 
            stroke={graphData.color || "#3b82f6"} 
            strokeWidth="2" 
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        
        {/* タイトル */}
        <text 
          x="200" 
          y="30" 
          fill={textColor} 
          fontSize="14" 
          textAnchor="middle" 
          fontWeight="bold"
        >
          {graphData.title || "二次曲線"}
        </text>
      </svg>
    </div>
  );
};

export default GraphDisplay;